import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuthOrg } from "../context/AuthOrgContext";
import { toast } from "../common/Popover";
import authService from "../apiServices/authService";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { TrendingUp } from "lucide-react";

const CertLogin: React.FC = () => {
    const navigate = useNavigate();
    const { login: orgLogin, isAuthenticated: isOrgAuthenticated } = useAuthOrg();

    const [formData, setFormData] = useState({
        usernameOrEmail: "",
        password: "",
    });
    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

    // Redirect if already authenticated
    React.useEffect(() => {
        if (isOrgAuthenticated) {
            navigate("/home", { replace: true });
        }
    }, [isOrgAuthenticated, navigate]);

    const validateForm = () => {
        const newErrors: { email?: string; password?: string } = {};

        if (!formData.usernameOrEmail) {
            newErrors.email = "Username or Email is required";
        }

        if (!formData.password) {
            newErrors.password = "Password is required";
        } else if (formData.password.length < 4) {
            newErrors.password = "Password must be at least 6 characters";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear error on typing
        if (errors[name as keyof typeof errors]) {
            setErrors((prev) => ({ ...prev, [name]: undefined }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        setLoading(true);
        try {
            // Call login API with encrypted password
            const response = await authService.login(formData.usernameOrEmail, formData.password);

            // Check if login was successful
            if (response.meta.status && response.meta.code === 200) {
                // Pass the response data to context login method
                orgLogin({
                    token: response.meta.newToken,
                    userDetails: response.data.userDetails,
                    organizationDetails: response.data.organizationDetails,
                });

                toast('success', response.meta.message || 'Login successful', 2);
                navigate('/home', { replace: true });
            } else {
                toast('error', response.meta.message || 'Login failed', 3);
            }
        } catch (error: any) {
            const message =
                error.message ||
                "Invalid credentials. Please try again.";
            toast("error", message, 3);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex">
            {/* Left Side - Branding */}
            <div className="hidden md:flex md:w-1/2 bg-indigo-900 text-white flex-col justify-center px-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#1F344C] to-[#1F344C] opacity-90"></div>
                <div className="relative z-10">
                    <h1 className="flex items-center justify-center gap-3 text-5xl font-bold mb-4 pr-14">
                        <TrendingUp className="text-5xl" size={40} />
                        <span>Times Engage</span>
                    </h1>
                    <h2 className="text-3xl font-light mb-6 text-center">Power Every Conversation</h2>
                    <p className="text-xl text-indigo-200 text-center">Turn customer conversations into business outcomes</p>
                </div>
                {/* Decorative circles */}
                <div className="absolute top-0 left-0 w-64 h-64 bg-white opacity-5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-500 opacity-10 rounded-full translate-x-1/3 translate-y-1/3"></div>
            </div>

            {/* Right Side - Login Form */}
            <div className=" w-full md:w-1/2 bg-white flex flex-col justify-center items-center p-8 md:p-12 relative">
                <div className="absolute top-6 w-full text-center m-auto mt-6">
                    <img src="https://tml-ad.s3.ap-south-1.amazonaws.com/ac526d81-fe38-11ee-a871-0a71828867bb/timesEngage_crosshair_1788356558106.png" alt="LOGO" className="w-48 m-auto" />
                    {/* <p className="text-xs text-gray-400">
                        TimesEngage © 2026, All rights reserved.
                    </p> */}
                </div>
                <div className="w-full max-w-md space-y-8 shadow-xl rounded-lg p-6">
                    <div className="text-left">
                        <h2 className="text-3xl font-bold text-gray-900">Login in</h2>
                        <p className="mt-2 text-sm text-gray-600">
                            Please enter your details to access your account
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-5">
                            <div>
                                {/* <label htmlFor="usernameOrEmail" className="block text-sm font-medium text-gray-700">
                                    Username
                                </label> */}
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="usernameOrEmail"
                                        name="usernameOrEmail"
                                        type="text"
                                        autoComplete="username"
                                        required
                                        value={formData.usernameOrEmail}
                                        onChange={handleChange}
                                        className={`appearance-none block w-full pl-10 px-3 py-3 border ${errors.email ? 'border-red-300' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors`}
                                        placeholder="Enter username"
                                    />
                                </div>
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                                )}
                            </div>

                            <div>
                                {/* <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                                    Password
                                </label> */}
                                <div className="mt-1 relative rounded-md shadow-sm">
                                    <input
                                        id="password"
                                        name="password"
                                        type="password"
                                        autoComplete="current-password"
                                        required
                                        value={formData.password}
                                        onChange={handleChange}
                                        className={`appearance-none block w-full pl-10 px-3 py-3 border ${errors.password ? 'border-red-300' : 'border-gray-300'} rounded-lg placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm transition-colors`}
                                        placeholder="Enter password"
                                    />
                                </div>
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                                )}
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="text-sm text-gray-900 ml-2">
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <Link to="/forgot-password" className="font-medium text-indigo-600 hover:text-indigo-500">
                                    Forgot password?
                                </Link>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className={`w-full flex justify-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white ${loading ? 'bg-[#1F344C] cursor-not-allowed' : 'bg-[#1F344C] hover:bg-[#1F344C] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'} transition-colors`}
                            >
                                {loading ? 'Signing in...' : 'Sign in'}
                            </button>
                        </div>
                    </form>

                </div>

                {/* Footer */}
                <div className="absolute bottom-6 w-full text-center m-auto">
                    {/* <img src="https://tml-ad.s3.ap-south-1.amazonaws.com/ac526d81-fe38-11ee-a871-0a71828867bb/timesEngage_crosshair_1788356558106.png" alt="LOGO" className="w-24 m-auto" /> */}
                    <p className="text-xs text-gray-400">
                        TimesEngage © 2026, All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default CertLogin;
